<template>
  <ak-modal-body>
    <ak-modal-title class="text-grey">
      Installation Instructions
    </ak-modal-title>
    <p>
      Install your page onto a website by embedding it into existing markup or
      creating a new webpage.
    </p>
    <ak-button-group ghost class="one-whole">
      <ak-button
        :ghost="isNew"
        color="grey-light"
        class="one-half"
        @click="toggle"
      >
        Existing Webpage
      </ak-button>
      <ak-button
        :ghost="isExisting"
        color="grey-light"
        class="one-half"
        @click="toggle"
      >
        New Webpage
      </ak-button>
    </ak-button-group>
    <ak-box class="p-none" background="grey-lightest" :class="arrow">
      <ak-box class="text-center" narrow>
        <strong>
          <template v-if="isExisting">
            Paste this code into the website markup
          </template>
          <template v-if="isNew">
            Create a blank new webpage and paste in this code
          </template>
        </strong>
      </ak-box>
      <textarea
        v-if="isExisting"
        id="embed-existing"
        ref="embedExistingElement"
        :value="embedExisting(container)"
        readonly
        class="code-textarea embed-code"
        rows="7"
        @click.stop.prevent="copyText(embedExistingElement)"
      />
      <textarea
        v-if="isNew"
        id="embed-new"
        ref="embedNewElement"
        :value="embedNew(container)"
        readonly
        class="code-textarea embed-code"
        rows="16"
        @click.stop.prevent="copyText(embedNewElement)"
      />
    </ak-box>
    <ak-box
      v-if="isExisting"
      rounded
      narrow
      background="info-light"
      class="mt-small"
    >
      <div class="flex">
        <ak-icon
          type="information-circled"
          color="info"
          size="big"
          class="mr-tiny"
        />
        <div
          class="form-label text-small text-info"
          v-text="
            'If your website is responsively designed, be sure to include the following meta tag in the <head> element'
          "
        ></div>
      </div>
      <textarea
        id="embed-head"
        ref="embedHeadElement"
        :value="embedHead()"
        readonly
        class="code-textarea embed-head"
        rows="2"
        @click.stop.prevent="copyText(embedHeadElement)"
      />
    </ak-box>
  </ak-modal-body>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalTitle from '@akerolabs/akero-vue/src/components/modal/ModalTitle.vue'
import ButtonGroup from '@akerolabs/akero-vue/src/components/buttonGroup/ButtonGroup.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import { embedNew, embedExisting, embedHead } from '../utils'
export default defineComponent({
  name: 'InstallContainer',
  components: {
    [ModalBody.name]: ModalBody,
    [ModalTitle.name]: ModalTitle,
    [ButtonGroup.name]: ButtonGroup,
    [Button.name]: Button,
    [Box.name]: Box,
    [Icon.name]: Icon,
  },
  props: {
    container: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const view = ref('existing')
    const embedExistingElement = ref(null)
    const embedNewElement = ref(null)
    const embedHeadElement = ref(null)
    const isExisting = computed(() => {
      return view.value === 'existing'
    })
    const isNew = computed(() => {
      return view.value === 'new'
    })

    const toggle = () => {
      if (view.value === 'existing') {
        view.value = 'new'
      } else if (view.value === 'new') {
        view.value = 'existing'
      }
    }

    const arrow = computed(() => {
      return {
        'box--has-arrow': true,
        'box--has-arrow-right': isNew.value,
      }
    })

    const copyText = (element) => {
      element.select()
      element.setSelectionRange(0, 99999)
      navigator.clipboard.writeText(element.value)
    }

    return {
      isExisting,
      isNew,
      toggle,
      arrow,
      embedNew,
      embedExisting,
      embedHead,
      copyText,
      embedExistingElement,
      embedNewElement,
      embedHeadElement,
    }
  },
})
</script>

<style lang="scss">
@import '@akerolabs/akero-vue/src/scss/_settings.scss';
.box--has-arrow {
  &:before {
    border-bottom-color: $color-grey-lightest !important;
  }
}
.embed-code {
  border: none !important;
  border-radius: 0 !important;
  cursor: copy;
}

.embed-head {
  cursor: copy;
  background-color: white !important;
  border: none !important;
}
</style>
